import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, AlertCircle, Lightbulb, ClipboardList, ArrowRight } from 'lucide-react';
import { SPORT_STYLES, SPORT_FALLBACK } from './constants';
import suggestCourtsForPending from './suggestCourts';
import { formatTimeRange } from '../BookingSeries/seriesHelpers';

/**
 * Compact merged stat + alert banner. Shows a single row with:
 *   📋 [N PENDING]  [Nx Badminton] [Mx Pickleball]   [▼ details] [Assign Courts] [View all]
 * When count = 0, shows a slim row "0 pending" only.
 */
export default function PendingAllocationStrip({ items, schedule, locationId, date }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const buildPendingUrl = () => {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (locationId) params.set('location_id', locationId);
    return `/admin/pending?${params.toString()}`;
  };

  const goToPending = () => navigate(buildPendingUrl());
  const count = items?.length || 0;

  const sportCounts = (items || []).reduce((acc, b) => {
    const s = b.primary_sport || 'unknown';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Empty state — slim 1-row card
  if (count === 0) {
    return (
      <div
        className="border-2 border-gray-300 bg-white rounded-2xl px-3 py-2 flex items-center justify-between gap-3 print:hidden"
        data-testid="stat-pending"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-gray-500" />
          <span className="text-xs uppercase font-bold text-muted-foreground">
            Pending Allocation
          </span>
          <span className="text-base font-black leading-none">0</span>
        </div>
        <button
          type="button"
          onClick={goToPending}
          data-testid="stat-pending-view-all-pill"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold uppercase tracking-wide transition-colors"
        >
          View all <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Active pending state — merged stat + alert banner
  return (
    <div
      className="border-2 border-amber-300 bg-amber-50 rounded-2xl px-3 py-2 print:break-inside-avoid"
      data-testid="pending-allocation-strip"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
          <span className="font-bold text-amber-900 text-sm">
            {count} request{count !== 1 ? 's' : ''} awaiting court assignment
          </span>
          <div className="flex gap-1 flex-wrap">
            {Object.entries(sportCounts).map(([sport, n]) => {
              const sty = SPORT_STYLES[sport] || SPORT_FALLBACK;
              return (
                <Badge
                  key={sport}
                  className={`${sty.badge} text-[10px] py-0`}
                  data-testid={`pending-sport-badge-${sport}`}
                >
                  {n}× {sty.label}
                </Badge>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={() => setExpanded((v) => !v)}
            data-testid="pending-allocation-toggle"
            aria-label={expanded ? 'Hide details' : 'Show details'}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            className="bg-emerald-700 hover:bg-emerald-800 text-white h-7"
            onClick={goToPending}
            data-testid="goto-pending-btn"
          >
            Assign Courts <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
          <button
            type="button"
            onClick={goToPending}
            data-testid="stat-pending-view-all-pill"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 text-[10px] font-bold uppercase tracking-wide transition-colors"
            title="Open Pending Requests page"
          >
            View all
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 border-t border-amber-200 pt-2 space-y-1.5">
          {items.map((b) => {
            const sty = SPORT_STYLES[b.primary_sport] || SPORT_FALLBACK;
            const suggestion = suggestCourtsForPending(b, schedule);
            return (
              <div
                key={b.id}
                className="flex flex-wrap items-center gap-2 text-xs bg-white rounded-lg px-2 py-1.5 border"
                data-testid={`pending-row-${b.booking_ref}`}
              >
                <Badge className={`${sty.badge} text-[10px]`}>{sty.label}</Badge>
                <span className="font-mono text-[10px]">#{b.booking_ref}</span>
                <span className="font-semibold">{b.customer_name}</span>
                <span className="text-muted-foreground">·</span>
                <span>{formatTimeRange(b.time_slot, b.duration)}</span>
                <span className="text-muted-foreground">·</span>
                <span>
                  {b.duration}h · {b.courts_requested?.reduce((s, c) => s + (c.quantity || 1), 0) || 1}{' '}
                  court{(b.courts_requested?.reduce((s, c) => s + (c.quantity || 1), 0) || 1) > 1 ? 's' : ''}
                </span>
                {b.special_requests && (
                  <span className="text-[10px] text-muted-foreground italic">
                    "{b.special_requests}"
                  </span>
                )}
                {suggestion && suggestion.courts.length > 0 && (
                  <Badge
                    variant="outline"
                    className="ml-auto bg-emerald-50 border-emerald-300 text-emerald-900 gap-1 cursor-default text-[10px]"
                    title={suggestion.reason}
                    data-testid={`suggest-court-${b.booking_ref}`}
                  >
                    <Lightbulb className="w-3 h-3" />
                    Try Court {suggestion.courts.join(', ')} — {suggestion.reason}
                  </Badge>
                )}
                {suggestion && suggestion.courts.length === 0 && (
                  <Badge
                    variant="outline"
                    className="ml-auto bg-red-50 border-red-300 text-red-900 text-[10px]"
                    data-testid={`suggest-court-${b.booking_ref}-none`}
                  >
                    {suggestion.reason}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
